import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { X, Check, BarChart3, Sun, Moon, Globe, Wallet, Send, EyeOff, AlertTriangle, Zap, CreditCard, Download, ArrowLeft, ScanLine, Bell, LogOut, FileText, Cookie } from 'lucide-react';
import NotificationCenter, { SELECT_CHEVRON } from './NotificationCenter';
import { useLanguage, LANGUAGES } from '../../i18n';
import useCurrencyPreference from '../../hooks/useCurrencyPreference';
import { useTimezonePreference, isValidTimezone } from '../../utils/timezone';
import { adoptServerPreferences } from '../../utils/preferenceSync';
import { useRetentionPolicy } from '../../utils/retentionPolicy';
import { Clock, Trash2, Loader2, RotateCcw, UserCog, Settings } from 'lucide-react';
import apiService from '../../services/apiService';
import telegramService from '../../services/telegramService';
import QRCode from '../QRCode';
import LegalLinks, { useCookiePreferences } from '../LegalLinks';
import ChangeSalaryDialog from './ChangeSalaryDialog';
import InfoTooltip from '../InfoTooltip';
import { consumedPercent, planLabel, planValue } from '../../utils/planDisplay';
import { userErrorMessage } from '../../utils/userError';
import Modal from '../ui/Modal';
import SecuritySection from '../settings/SecuritySection';

// ── The settings-row vocabulary (design §12) ────────────────────────────────
// A group is a card of hairline-divided rows under an eyebrow heading; a row is
// 17px/22px with a 15/600 label, a 13px meta description, and a control on the
// right. Kept as constants so every one of the sixteen groups below is
// identical by construction rather than by copy-paste.
const GROUP_CARD = 'rounded-card border border-pai bg-white/[0.025] overflow-hidden';
// #432: on a phone-width modal (< 480px) a row's control may not fit beside its
// label — the label used to be crushed to ~20px with its words painted over the
// control. There the row WRAPS: the control drops under the label. ≥ 480px the
// modal is at its full max-w-md and nothing changes.
const ROW = 'flex items-center justify-between gap-5 px-[22px] py-[17px] border-b border-pai-hairline last:border-b-0 max-[479px]:flex-wrap max-[479px]:gap-y-3';
const ROW_START = 'flex items-start justify-between gap-5 px-[22px] py-[17px] border-b border-pai-hairline last:border-b-0 max-[479px]:flex-wrap max-[479px]:gap-y-3';
const ROW_MAIN = 'flex items-center gap-3 min-w-0 max-[479px]:flex-1 max-[479px]:min-w-[10rem]';
const ROW_LABEL = 'text-[15px] font-semibold text-white';
const ROW_DESC = 'mt-1 text-[13px] text-pai-meta';
const EYEBROW = 'pai-eyebrow block mb-3';
// Condense information (#390): the channel boxes (PAI Tools / PAI
// Conversations) keep only their label + control inline; the explanatory
// prose moved behind a "?" beside the section heading, so the row needs far less
// padding than the full-height ROW above.
const ROW_COMPACT = 'flex items-center justify-between gap-4 px-[22px] py-3 border-b border-pai-hairline last:border-b-0 max-[479px]:flex-wrap max-[479px]:gap-y-2';
const EYEBROW_ROW = 'flex items-center gap-1 mb-3';
// h-40 / radius-11 / `bg-white/[0.03]` per the design. The ground is written as
// the opaque #12161B — that IS white/3% composited over the modal ground
// (#0B0F14) — so the native <option> popup stays readable instead of inheriting
// a translucent fill.
const SELECT = 'h-10 max-w-full appearance-none rounded-ctl border border-pai bg-[#12161b] pl-4 pr-9 text-[14px] font-medium text-white transition-colors hover:border-white/30 focus:outline-none focus:border-white/80';
const OPTION = 'bg-pai-modal text-white';

// The PAI switch: 52×30 track, 24px knob, 3px padding. Both states carry a 1px
// border so flipping it never changes the box size.
const switchTrack = (on) => `relative inline-flex h-[30px] w-[52px] items-center rounded-full border p-[3px] transition-colors duration-150 focus:outline-none focus-visible:border-white/80 flex-shrink-0 ${on ? 'border-transparent bg-white/[0.3]' : 'border-pai bg-white/[0.06]'}`;
const switchKnob = (on) => `inline-block h-6 w-6 transform rounded-full transition-transform duration-150 ${on ? 'translate-x-[22px] bg-white' : 'translate-x-0 bg-white/45'}`;

/**
 * Centered glass-morphism settings modal. Backdrop is blurred so the user
 * can still see the silhouette of their work behind it — settings feel like
 * a momentary overlay, not a destination.
 *
 * Settings Update (#236): ONE component, TWO structurally-separate views driven
 * by `mode`:
 *   - 'user'   — account/identity/billing: Profile (name + email), Salary
 *                & Payment, Telegram, Privacy (Ghost Mode),
 *                Delete Account (danger zone), Logout. Opened from the rail's
 *                account bubble → "User Settings".
 *   - 'system' — app-wide prefs: Notifications, Appearance/Theme, Language,
 *                Base Currency, Timezone, Recently-Deleted retention, AI
 *                Personalization, and the operator-only Usage Dashboard (the two
 *                founder accounts). Opened from the
 *                rail's gear (⌘,).
 * Every existing data-testid is preserved — a field simply lives in one view now.
 *
 * ESC closes. Backdrop click closes.
 */
// Light mode is SUSPENDED until it has a design of its own — the app paints dark
// grounds unconditionally, so a light theme is unreadable rather than merely
// off-brand. The control is hidden rather than deleted: `useTheme`'s
// LIGHT_MODE_SUPPORTED flag is the counterpart, and flipping both restores it.
const SHOW_THEME_TOGGLE = false;

const SettingsModal = ({ isOpen, mode = 'system', onClose, theme = 'light', onToggleTheme, onOpenEnergyPanel, onLogout }) => {
    const navigate = useNavigate();
    const { language, setLanguage, t } = useLanguage();
    const { currency: baseCurrency, setCurrency, supported: currencyList } = useCurrencyPreference();
    const { timezone, setTimezone, supported: timezoneList } = useTimezonePreference();
    const { policy: retentionPolicy, setPolicy: setRetentionPolicy, options: retentionOptions } = useRetentionPolicy();
    // #401: the way back into iubenda's consent panel, now that its floating
    // badge is off. `available` is false until the CDN widget has loaded.
    const { available: cookiePrefsAvailable, open: openCookiePreferences } = useCookiePreferences();

    // #480 — "Saved" confirmation, shown ONLY after a successful server save of
    // the preferred currency or the timezone (PO rule: no save notification for
    // Language, Notification Center or anything else). A failed save shows the
    // functional/technical error (Key Pattern 13c) in the same slot; the value
    // itself is already reverted by the preference hook. Portaled to <body> and
    // rendered even after the modal closes, so an Escape right after a change
    // still shows the confirmation.
    const [prefToast, setPrefToast] = useState(null);   // { kind: 'saved'|'error', message }
    const prefToastTimerRef = useRef(null);
    const showPrefToast = useCallback((kind, message) => {
        if (prefToastTimerRef.current) clearTimeout(prefToastTimerRef.current);
        setPrefToast({ kind, message });
        prefToastTimerRef.current = setTimeout(() => setPrefToast(null), 4000);
    }, []);
    useEffect(() => () => { if (prefToastTimerRef.current) clearTimeout(prefToastTimerRef.current); }, []);
    const confirmPreferenceSave = useCallback((promise) => {
        Promise.resolve(promise)
            .then((saved) => { if (saved) showPrefToast('saved', t('preferenceSaved', 'Saved')); })
            .catch((err) => showPrefToast('error', userErrorMessage(err, t)));
    }, [showPrefToast, t]);

    // Timezone input (#480): typing used to write EVERY keystroke ("E", "Eu", …)
    // straight into the stored preference. It is now a draft, committed only as a
    // real IANA zone — an exact datalist match while typing, or on blur/Enter —
    // and an emptied field clears the pin back to auto-detect.
    // Compared against the PIN, not the displayed zone: explicitly picking the
    // zone that is currently auto-detected still pins it (so it follows the user
    // to a device in another zone). `tzEditedRef` keeps a mere focus+blur from
    // pinning anything.
    const [tzDraft, setTzDraft] = useState(timezone);
    const tzEditedRef = useRef(false);
    useEffect(() => { setTzDraft(timezone); }, [timezone]);
    const readTzPin = () => {
        try { return localStorage.getItem('omnis:timezone'); } catch (_) { return null; }
    };
    const commitTimezone = useCallback((value) => {
        if (!tzEditedRef.current) return;
        tzEditedRef.current = false;
        const v = (value || '').trim();
        const pinned = readTzPin();
        if (!v) {
            if (pinned) confirmPreferenceSave(setTimezone(null));
            else setTzDraft(timezone);
            return;
        }
        if (v === pinned) { setTzDraft(timezone); return; }
        if (!isValidTimezone(v)) { setTzDraft(timezone); return; }
        confirmPreferenceSave(setTimezone(v));
    }, [timezone, setTimezone, confirmPreferenceSave]);

    // Unified Settings (#429). `mode` is now only the tab the modal OPENS on — the
    // avatar entry asks for 'user', every gear entry for 'system' — and the in-modal
    // toggle switches `activeMode` from there. It re-syncs to the requested mode
    // every time the modal (re)opens, and whenever a caller asks for a different
    // one while it is already open (⌘, while on the User tab). This is done during
    // render rather than in an effect on purpose: an effect would paint one frame of
    // the PREVIOUS session's tab before correcting it (the same reason the #285
    // reset below runs on CLOSE, not on open).
    const [activeMode, setActiveMode] = useState(mode === 'user' ? 'user' : 'system');
    const [openSig, setOpenSig] = useState(isOpen ? mode : null);
    const sig = isOpen ? mode : null;
    if (sig !== openSig) {
        setOpenSig(sig);
        if (isOpen) setActiveMode(mode === 'user' ? 'user' : 'system');
    }

    // Telegram link (deep-link handshake — bots can't see phone numbers, so
    // Connect mints a code via POST /api/telegram/link, opens the t.me link,
    // and we poll the profile until the webhook's /start handler binds it).
    const [telegramLinked, setTelegramLinked] = useState(false);
    const [telegramBusy, setTelegramBusy] = useState(false);
    const [telegramAwaiting, setTelegramAwaiting] = useState(false);
    const [telegramError, setTelegramError] = useState(null);
    // PAI Conversations — the fast-track bot (Dual-Bucket System). A SECOND,
    // independent bot, so it carries its own link state end to end: connecting
    // or disconnecting one world must never touch the other.
    const [convoLinked, setConvoLinked] = useState(false);
    const [convoBusy, setConvoBusy] = useState(false);
    const [convoAwaiting, setConvoAwaiting] = useState(false);
    const [convoError, setConvoError] = useState(null);

    // Telegram QR linking (#327). Connect used to `window.open` the t.me deep
    // link, which on a computer launches (or nags to install) the Telegram
    // DESKTOP client — the friction the card names. It now opens a dedicated
    // screen rendering that SAME link as a scannable QR, so the handshake
    // happens on the phone that already has Telegram and the desktop client is
    // never needed. The link itself stays available as an explicit fallback for
    // anyone who does want the desktop app.
    //
    // ONE screen serves BOTH bots — they differ only in which route mints the
    // code and which linked flag ends the wait — so `qrWorld` is the
    // discriminator ('tools' | 'convo'); null means the screen is closed.
    const [qrWorld, setQrWorld] = useState(null);
    const [qrLink, setQrLink] = useState(null);      // { deep_link, expires_at }
    const [qrBusy, setQrBusy] = useState(false);
    const [qrError, setQrError] = useState(null);
    // Ticks once a second while the screen is open so the countdown moves and
    // the expiry flips on its own. Reading Date.now() (rather than decrementing
    // a counter) keeps it honest across a backgrounded tab.
    const [qrNow, setQrNow] = useState(() => Date.now());

    // Ghost Mode (Agenda TO Contacts — Phase A, T7): hide this account from
    // contact discovery by email. Seeded from GET /auth/profile on open, saved via
    // PATCH /auth/profile {ghost_mode} (optimistic; reverts on failure).
    const [ghostMode, setGhostMode] = useState(false);
    const [ghostSaving, setGhostSaving] = useState(false);

    // 2FA (#333): the profile's `mfa` block ({enabled, recovery_codes_remaining})
    // for Settings → Security; the section owns everything else.
    const [mfaInfo, setMfaInfo] = useState(null);

    // Plan & Energy (Energy caps v2): read-only snapshot from GET /auth/profile
    // — {plan, effective_cap_usd, cycle_spent_usd, cycle_start}. Plans change
    // only via the admin endpoint / (later) the payment flow, never here.
    const [planStatus, setPlanStatus] = useState(null);

    // The Usage Dashboard is OPERATOR-ONLY (PO decision 2026-10-01) — the whole
    // Admin section renders only for an allowlisted operator account (the two
    // founders).
    //
    // ⚠️ Gated on `monitoring_access` from GET /auth/profile, NEVER on `role`.
    // The allowlist names the same accounts the admin role does today, but the
    // role is not the rule and a third admin would inherit the compute bill.
    // The BE 404s every /api/usage/* call from anyone else, so this only
    // decides whether a dead-end entry point is shown.
    const [hasMonitoringAccess, setHasMonitoringAccess] = useState(false);

    // Notification Center & Delivery Matrix (#191) — a dedicated overlay opened
    // from the Settings list.
    const [notifCenterOpen, setNotifCenterOpen] = useState(false);
    // Payments do not exist yet (they are their own cards), so this dialog
    // EXPLAINS the tiers rather than pretending to switch between them.
    const [changeSalaryOpen, setChangeSalaryOpen] = useState(false);

    // Profile (User Settings #184): the editable name PAI uses to address the
    // user (drives the account menu + avatar initials) + the read-only registered
    // email. Seeded from GET /auth/profile on open; name saved via PATCH.
    const [userName, setUserName] = useState('');
    const [email, setEmail] = useState('');
    const [nameSaving, setNameSaving] = useState(false);
    const [nameSaved, setNameSaved] = useState(false);
    const [nameError, setNameError] = useState(null);

    // Danger Zone — permanent account deletion (Agenda TO Contacts Phase D / T1
    // "Smithereens"). Two-step flow (User Settings #184 "Data Sovereignty"): the
    // modal FIRST offers a full-data ZIP export, THEN gates the destructive DELETE
    // behind an exact typed "DELETE" string; on success we sign the user out via
    // the existing auth/logout flow (drops them at the login screen).
    const [showDeleteAccount, setShowDeleteAccount] = useState(false);
    const [deleteStep, setDeleteStep] = useState('export'); // 'export' → 'confirm'
    const [exportBusy, setExportBusy] = useState(false);
    const [exportDone, setExportDone] = useState(false);
    const [exportError, setExportError] = useState(null);
    const [deleteConfirmText, setDeleteConfirmText] = useState('');
    const [deleteBusy, setDeleteBusy] = useState(false);
    const [deleteError, setDeleteError] = useState(null);

    // ── Telegram QR handshake (#327) ─────────────────────────────────
    // Declared HERE, above the Escape effect, because that effect lists
    // closeTelegramQr in its dependency array — a `const` referenced from a dep
    // array is read during RENDER, so defining these further down (beside the
    // other Telegram callbacks) is a temporal-dead-zone crash that takes the
    // whole shell with it, not a lint nit.

    // Mint a fresh code for one world and show its QR. Also arms that world's
    // awaiting flag, so the profile polling below keeps running and the screen
    // closes itself the moment the webhook's /start binds the account.
    const mintQrLink = useCallback(async (world) => {
        setQrBusy(true);
        setQrError(null);
        try {
            const res = world === 'convo'
                ? await telegramService.createConvoLink()
                : await telegramService.createLink();
            if (res?.deep_link) {
                setQrLink({ deep_link: res.deep_link, expires_at: res.expires_at || null });
                setQrNow(Date.now());
                if (world === 'convo') setConvoAwaiting(true);
                else setTelegramAwaiting(true);
                return;
            }
            // 503 with no deep link = that bot isn't configured server-side.
            setQrError(world === 'convo'
                ? t('navConvoNotConfigured', 'PAI Conversations is not configured')
                : t('navTelegramNotConfigured', 'PAI Tools is not configured'));
        } catch (e) {
            setQrError(userErrorMessage(e, t));
        } finally {
            setQrBusy(false);
        }
    }, [t]);

    const openTelegramQr = useCallback(async (world) => {
        setQrWorld(world);
        setQrLink(null);
        setQrError(null);
        await mintQrLink(world);
    }, [mintQrLink]);

    const closeTelegramQr = useCallback(() => {
        setQrWorld(null);
        setQrLink(null);
        setQrError(null);
    }, []);

    // A code is single-use and short-lived, so "refresh" is simply "mint
    // another" — the previous one is overwritten server-side (one pending code
    // per account per world), which is also why nothing needs revoking.
    const refreshTelegramQr = useCallback(() => {
        if (!qrWorld) return;
        mintQrLink(qrWorld);
    }, [qrWorld, mintQrLink]);

    useEffect(() => {
        if (!isOpen) return;
        const handleKey = (e) => {
            if (e.key === 'Escape') {
                // Esc backs out of a sub-screen first, then closes Settings.
                // (ChangeSalaryDialog handles its own Escape in the capture
                // phase and stops it, so it never reaches this handler.)
                if (notifCenterOpen) { setNotifCenterOpen(false); return; }
                if (qrWorld) { closeTelegramQr(); return; }
                onClose();
            }
        };
        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, [isOpen, onClose, notifCenterOpen, qrWorld, closeTelegramQr]);

    // #285 Settings Bug: sub-navigation must NOT survive a close. The modal
    // stays mounted for the whole session (`isOpen` merely hides it), so any
    // overlay/sub-screen state — the Notification Center overlay, the Telegram
    // QR screen, the delete-account modal — persisted across a close and
    // a later "Settings" click landed the user back in that stale sub-screen
    // instead of the root view of the requested mode. Reset the entire
    // navigation stack the moment the modal closes so every entry point is
    // clean. (Reset on CLOSE, not open: an on-open effect runs after paint, so a
    // stale overlay would still flash for one frame.)
    const resetNavigation = useCallback(() => {
        setNotifCenterOpen(false);
        setChangeSalaryOpen(false);
        setQrWorld(null);          // #327 — the QR screen is sub-navigation too
        setQrLink(null);
        setQrError(null);
        setShowDeleteAccount(false);
        setDeleteStep('export');
        setDeleteConfirmText('');
        setDeleteError(null);
        setExportError(null);
        setExportDone(false);
    }, []);

    useEffect(() => {
        if (isOpen) return;
        resetNavigation();
    }, [isOpen, resetNavigation]);

    // Unified Settings (#429): switching tab lands on the ROOT of the new tab, so
    // a Notification Center / Telegram QR / delete-account screen opened on the old
    // tab can never be left stranded over the new one.
    const switchMode = useCallback((next) => {
        if (next === activeMode) return;
        resetNavigation();
        setActiveMode(next);
    }, [activeMode, resetNavigation]);

    // Payment #336: the Salary section must reflect a new tier WITHOUT a page
    // refresh. The profile fetch below is keyed on `isOpen`, so a plan change
    // that lands while the modal is already OPEN (the Stripe webhook arriving a
    // second or two after the user returns, or a change made elsewhere) would
    // otherwise leave a stale tier on screen until the modal was closed and
    // reopened. `omnis:plan-updated` — dispatched by WorkspaceShell's billing
    // poll the moment the plan actually flips — bumps this nonce, which the
    // effect depends on. Same omnis:* window-event idiom every other panel uses
    // for live updates, so nothing new had to be invented.
    const [profileNonce, setProfileNonce] = useState(0);
    const [billingBusy, setBillingBusy] = useState(false);
    const [billingError, setBillingError] = useState(null);

    // One click to the REAL invoice history. Mirrors ChangeSalaryDialog's portal
    // call, including its 409 case: a plan that was granted rather than bought
    // has no billing account, and saying so beats a silent no-op.
    const openBillingPortal = async () => {
        setBillingError(null);
        setBillingBusy(true);
        try {
            const billingService = (await import('../../services/billingService')).default;
            const { url } = await billingService.createPortal();
            window.location.assign(url);
        } catch (err) {
            setBillingError(
                err?.status === 409
                    ? t('salaryNoBilling',
                        'No billing account yet — your current tier was granted, not purchased. Pick a tier to hire PAI.')
                    : t('salaryError', 'Could not start checkout — please try again.'));
            setBillingBusy(false);
        }
    };

    useEffect(() => {
        const bump = () => setProfileNonce((n) => n + 1);
        window.addEventListener('omnis:plan-updated', bump);
        return () => window.removeEventListener('omnis:plan-updated', bump);
    }, []);

    useEffect(() => {
        if (!isOpen) return undefined;
        let cancelled = false;
        const requestedAt = Date.now();
        apiService.getUserProfile()
            .then((p) => {
                if (cancelled) return;
                // #480 — a change made on another device since this tab loaded
                // (never over a local change made after this read started).
                adoptServerPreferences(p, { requestedAt });
                setUserName(p?.username || '');
                setEmail(p?.email || '');
                setNameSaved(false);
                setNameError(null);
                setGhostMode(!!p?.ghost_mode);
                setMfaInfo(p?.mfa || null);
                setTelegramLinked(!!p?.telegram_linked);
                setConvoLinked(!!p?.telegram_convo_linked);
                setPlanStatus(p?.plan_status || null);
                setHasMonitoringAccess(p?.monitoring_access === true);
            })
            .catch(() => { /* leave blank */ });
        return () => { cancelled = true; };
    }, [isOpen, profileNonce]);

    // While a Telegram deep link is out, poll the profile so the "Linked"
    // state flips the moment the user taps Start in Telegram.
    useEffect(() => {
        if (!isOpen || !telegramAwaiting || telegramLinked) return undefined;
        const interval = setInterval(async () => {
            try {
                const p = await apiService.getUserProfile();
                if (p?.telegram_linked) {
                    setTelegramLinked(true);
                    setTelegramAwaiting(false);
                }
            } catch (_) { /* keep polling */ }
        }, 3000);
        return () => clearInterval(interval);
    }, [isOpen, telegramAwaiting, telegramLinked]);

    // Same handshake for the fast-track bot, on its own flag.
    useEffect(() => {
        if (!isOpen || !convoAwaiting || convoLinked) return undefined;
        const interval = setInterval(async () => {
            try {
                const p = await apiService.getUserProfile();
                if (p?.telegram_convo_linked) {
                    setConvoLinked(true);
                    setConvoAwaiting(false);
                }
            } catch (_) { /* keep polling */ }
        }, 3000);
        return () => clearInterval(interval);
    }, [isOpen, convoAwaiting, convoLinked]);

    // #327 — drive the QR countdown while its screen is open (and only then:
    // an always-on second-timer would re-render the whole Settings modal for
    // nothing). Re-armed on a new link so the first tick is immediate.
    useEffect(() => {
        if (!isOpen || !qrWorld) return undefined;
        setQrNow(Date.now());
        const id = setInterval(() => setQrNow(Date.now()), 1000);
        return () => clearInterval(id);
    }, [isOpen, qrWorld, qrLink]);

    // #327 — the scan succeeded: the polling above flipped this world's linked
    // flag, so the QR has done its job. Close the screen so the user lands back
    // on the settings list with the row now reading "Linked".
    useEffect(() => {
        if (!qrWorld) return;
        if (qrWorld === 'convo' ? convoLinked : telegramLinked) closeTelegramQr();
    }, [qrWorld, telegramLinked, convoLinked, closeTelegramQr]);

    const unlinkTelegram = useCallback(async () => {
        setTelegramBusy(true);
        setTelegramError(null);
        try {
            await telegramService.unlink();
            setTelegramLinked(false);
            setTelegramAwaiting(false);
        } catch (e) {
            setTelegramError(userErrorMessage(e, t));
        } finally {
            setTelegramBusy(false);
        }
    }, [t]);

    const unlinkConvo = useCallback(async () => {
        setConvoBusy(true);
        setConvoError(null);
        try {
            await telegramService.unlinkConvo();
            setConvoLinked(false);
            setConvoAwaiting(false);
        } catch (e) {
            setConvoError(userErrorMessage(e, t));
        } finally {
            setConvoBusy(false);
        }
    }, [t]);

    const toggleGhostMode = useCallback(async () => {
        const next = !ghostMode;
        setGhostMode(next); // optimistic
        setGhostSaving(true);
        try {
            await apiService.patch('/auth/profile', { ghost_mode: next });
        } catch (_) {
            setGhostMode(!next); // revert on failure
        } finally {
            setGhostSaving(false);
        }
    }, [ghostMode]);

    const saveUserName = useCallback(async () => {
        const next = (userName || '').trim();
        if (!next) { setNameError(t('nameEmptyError', 'Name cannot be empty')); return; }
        setNameSaving(true);
        setNameError(null);
        setNameSaved(false);
        try {
            const p = await apiService.patch('/auth/profile', { username: next });
            setUserName(p?.username || next);
            setNameSaved(true);
            // Let the rest of the app (avatar/account menu) pick up the new name.
            // Listened for by `useAuth` since Hot reload (#397) — it merges the
            // detail into every live `user` instance, so the rail initials, the
            // name beside them, the mobile menu and the chat-bubble avatars all
            // move with no reload. The detail carries the value the SERVER
            // returned (trimmed + clipped to the column width), never the raw
            // draft, so what the app adopts is exactly what was stored.
            try { window.dispatchEvent(new CustomEvent('omnis:profile-updated', { detail: { username: p?.username || next } })); } catch (_) { /* ignore */ }
        } catch (e) {
            setNameError(userErrorMessage(e, t));
        } finally {
            setNameSaving(false);
        }
    }, [userName, t]);

    const openDeleteAccount = useCallback(() => {
        setDeleteStep('export');
        setExportBusy(false);
        setExportDone(false);
        setExportError(null);
        setDeleteConfirmText('');
        setDeleteError(null);
        setDeleteBusy(false);
        setShowDeleteAccount(true);
    }, []);

    const closeDeleteAccount = useCallback(() => {
        if (deleteBusy || exportBusy) return; // don't let it close mid-flight
        setShowDeleteAccount(false);
        setDeleteStep('export');
        setDeleteConfirmText('');
        setDeleteError(null);
        setExportError(null);
        setExportDone(false);
    }, [deleteBusy, exportBusy]);

    const downloadExport = useCallback(async () => {
        setExportBusy(true);
        setExportError(null);
        setExportDone(false);   // a Retry starts from a clean slate (#331)
        try {
            const { blob, filename } = await apiService.exportAccountData();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 10000);
            setExportDone(true);
        } catch (e) {
            // The archive is streamed, so a failure here is a network/server
            // problem rather than a partial file. Say so plainly and offer Retry
            // (#331). The raw status stays out of the UI (#439) — it is logged.
            console.error('Account export failed:', e);
            setExportError(userErrorMessage(e, t));
        } finally {
            setExportBusy(false);
        }
    }, [t]);

    const confirmDeleteAccount = useCallback(async () => {
        if (deleteConfirmText !== 'DELETE') return; // typed-gate guard (BE requires the exact string)
        setDeleteBusy(true);
        setDeleteError(null);
        try {
            await apiService.deleteAccount('DELETE');
            // Account is gone — sign out through the existing auth flow, which
            // tears down the session and routes back to the login screen.
            if (onLogout) {
                await onLogout();
            }
            setShowDeleteAccount(false);
            onClose();
        } catch (e) {
            setDeleteError(userErrorMessage(e, t));
            setDeleteBusy(false);
        }
    }, [deleteConfirmText, onLogout, onClose, t]);

    const prefToastNode = prefToast ? createPortal(
        <div
            data-testid={prefToast.kind === 'saved' ? 'preference-saved-toast' : 'preference-save-error'}
            role="status"
            aria-live="polite"
            className={`fixed bottom-4 right-4 z-[90] max-w-xs px-4 py-3 rounded-ctl shadow-modal-sm border text-sm inline-flex items-center gap-2 bg-pai-modal text-white ${prefToast.kind === 'saved' ? 'border-pai-green/40' : 'border-pai-red/40'}`}
        >
            {prefToast.kind === 'saved' && <Check size={15} className="text-pai-green flex-shrink-0" />}
            <span className="min-w-0 break-words">{prefToast.message}</span>
        </div>,
        document.body,
    ) : null;

    if (!isOpen) return prefToastNode;

    // #236 — which structural view this is. Default (unspecified) = 'system'.
    const isUser = activeMode === 'user';
    const isSystem = !isUser;
    // The heading is the neutral "Settings" now — the two tabs under it name the
    // category, so repeating "User Settings" here would just say it twice (#429).
    const viewTitle = t('settingsTitle', 'Settings');

    const doLogout = async () => {
        onClose();
        if (onLogout) await onLogout();
    };

    // Telegram QR screen (#327). Both bots share one screen, so the copy is
    // selected here rather than duplicated in the markup; the inner testids are
    // identical because only one screen is ever mounted.
    const qrIsConvo = qrWorld === 'convo';
    const qrCfg = qrIsConvo
        ? {
            screenTestId: 'telegram-convo-qr-screen',
            title: t('telegramConvoConnectTitle', 'Connect PAI Conversations'),
            intro: t('telegramConvoQrIntro', 'Scan this code with your phone to connect PAI Conversations — the fast track. Telegram opens on your phone; tap Start and this page updates itself.'),
            openLabel: t('telegramConvoQrOpen', 'Open PAI Conversations in Telegram'),
            alt: t('telegramConvoQrAlt', 'Scan to connect PAI Conversations'),
        }
        : {
            screenTestId: 'telegram-qr-screen',
            title: t('telegramConnectTitle', 'Connect PAI Tools'),
            intro: t('telegramQrIntro', 'Scan this code with your phone to connect PAI Tools. Telegram opens on your phone; tap Start and this page updates itself — no desktop app needed.'),
            openLabel: t('telegramQrOpen', 'Open PAI Tools in Telegram'),
            alt: t('telegramQrAlt', 'Scan to connect PAI Tools'),
        };
    // A code with no expiry is treated as live: the countdown is a courtesy, and
    // an older backend that doesn't send `expires_at` must not render every QR
    // as dead on arrival.
    const qrExpiresMs = qrLink?.expires_at ? Date.parse(qrLink.expires_at) : null;
    const qrMsLeft = Number.isFinite(qrExpiresMs) ? qrExpiresMs - qrNow : null;
    const qrExpired = qrMsLeft != null && qrMsLeft <= 0;
    const qrCountdown = (() => {
        if (qrMsLeft == null || qrExpired) return null;
        const total = Math.ceil(qrMsLeft / 1000);
        return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
    })();
    const qrShowsCode = !!qrLink?.deep_link && !qrExpired;
    const qrAwaiting = qrIsConvo ? convoAwaiting : telegramAwaiting;

    return (
        <>
        {prefToastNode}
        {/* Escape is handled by the effect above (it backs out of a sub-screen
            first), so the Settings <Modal> and its sub-dialogs leave it alone. */}
        <Modal
            onClose={onClose}
            zIndex="z-[70]"
            closeOnEscape={false}
            className="animate-backdrop-fade-in"
            cardClassName="p-7 animate-fade-slide-in max-h-[85dvh]"
        >
                <div className="flex items-center justify-between gap-3 mb-6">
                    <div className="flex items-center gap-3 min-w-0">
                        {qrWorld && (
                            <button
                                data-testid="telegram-qr-back"
                                onClick={closeTelegramQr}
                                className="pai-icon-btn w-10 h-10 border border-pai flex-shrink-0"
                                title={t('back', 'Back')}
                                aria-label={t('back', 'Back')}
                            >
                                <ArrowLeft size={18} />
                            </button>
                        )}
                        <h2 className="text-[27px] font-extrabold tracking-[-0.025em] text-white truncate">
                            {qrWorld ? qrCfg.title : viewTitle}
                        </h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="pai-icon-btn w-[38px] h-[38px] flex-shrink-0"
                        title={t('navCloseEsc', 'Close (Esc)')}
                    >
                        <X size={18} />
                    </button>
                </div>

                {/* Unified Settings (#429) — the two views behind one control. Shown
                    on the root view only: the drill-in screen (Telegram QR) replaces the list and own a Back arrow. */}
                {!qrWorld && (
                    <div
                        role="tablist"
                        aria-label={t('settingsTitle', 'Settings')}
                        data-testid="settings-tabs"
                        data-active={activeMode}
                        className="mb-6 -mt-2 grid grid-cols-2 gap-1 rounded-ctl border border-pai bg-white/[0.03] p-1"
                    >
                        {[
                            { key: 'user', testId: 'settings-tab-user', label: t('userSettings', 'User Settings'), Icon: UserCog },
                            { key: 'system', testId: 'settings-tab-system', label: t('systemSettings', 'System Settings'), Icon: Settings },
                        ].map(({ key, testId, label, Icon }) => {
                            const active = activeMode === key;
                            return (
                                <button
                                    key={key}
                                    type="button"
                                    role="tab"
                                    aria-selected={active}
                                    data-testid={testId}
                                    data-active={active ? 'true' : 'false'}
                                    onClick={() => switchMode(key)}
                                    className={`pai-tap-row min-w-0 inline-flex items-center justify-center gap-1.5 rounded-chip px-2.5 py-2 text-[13px] font-semibold transition-colors ${
                                        active
                                            ? 'bg-white/[0.12] text-white'
                                            : 'text-pai-secondary hover:bg-white/[0.06] hover:text-white'
                                    }`}
                                >
                                    <Icon size={14} className="flex-shrink-0" />
                                    <span className="truncate">{label}</span>
                                </button>
                            );
                        })}
                    </div>
                )}

                {/* Telegram QR linking screen (#327) — replaces the settings
                    list while active. Scanning happens on the phone; this page just
                    waits, so there is no "I've scanned it" button to press. */}
                {qrWorld && (
                    <div data-testid={qrCfg.screenTestId} className="space-y-4">
                        <p className="text-[15px] leading-relaxed text-pai-secondary">{qrCfg.intro}</p>

                        <div className="flex flex-col items-center gap-2">
                            <div
                                data-testid="telegram-qr-frame"
                                data-state={qrBusy ? 'loading' : (qrExpired ? 'expired' : (qrShowsCode ? 'ready' : 'error'))}
                                className="relative rounded-card border border-pai bg-white p-3"
                            >
                                {qrShowsCode ? (
                                    <QRCode
                                        key={qrLink.deep_link}
                                        value={qrLink.deep_link}
                                        size={180}
                                        data-testid="telegram-qr"
                                        title={qrCfg.alt}
                                    />
                                ) : (
                                    <div
                                        data-testid="telegram-qr-placeholder"
                                        className="flex items-center justify-center text-center text-xs text-pai-void/50 px-4"
                                        style={{ width: 180, height: 180 }}
                                    >
                                        {qrBusy ? t('telegramQrLoading', 'Generating your code…') : null}
                                    </div>
                                )}
                                {/* The expired state deliberately keeps the (now
                                    useless) symbol behind a scrim rather than
                                    blanking the screen: the user sees WHY the
                                    scan they just tried did nothing. */}
                                {qrExpired && (
                                    <div className="absolute inset-0 flex items-center justify-center rounded-card bg-white/80 pointer-events-none">
                                        <div className="flex items-center gap-1.5 rounded-full bg-black/70 px-3 py-1.5 text-xs font-medium text-white">
                                            <ScanLine size={14} /> {t('telegramQrExpiredBadge', 'Expired')}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {qrExpired ? (
                                <div data-testid="telegram-qr-expired" className="text-[12.5px] text-pai-amber text-center">
                                    {t('telegramQrExpired', 'This code has expired. Refresh it to get a new one.')}
                                </div>
                            ) : (qrCountdown && (
                                <div data-testid="telegram-qr-countdown" className="text-[12.5px] text-pai-meta tnum">
                                    {t('telegramQrExpiresIn', 'Code expires in {time}').replace('{time}', qrCountdown)}
                                </div>
                            ))}

                            {(qrAwaiting && !qrExpired) && (
                                <div data-testid="telegram-qr-awaiting" className="text-[12.5px] text-pai-meta text-center max-w-xs">
                                    {t('telegramQrAwaiting', 'Waiting for you to tap "Start" in Telegram — this page updates itself.')}
                                </div>
                            )}

                            {qrError && (
                                <div data-testid="telegram-qr-error" className="text-[12.5px] text-pai-red text-center max-w-xs">
                                    {qrError}
                                </div>
                            )}
                        </div>

                        <div className="flex items-center justify-between gap-2">
                            <button
                                data-testid="telegram-qr-cancel"
                                onClick={closeTelegramQr}
                                className="pai-btn-ghost"
                            >
                                {t('deleteAccountCancel', 'Cancel')}
                            </button>
                            <button
                                data-testid="telegram-qr-refresh"
                                onClick={refreshTelegramQr}
                                disabled={qrBusy}
                                className="pai-btn-primary disabled:opacity-40"
                            >
                                {qrBusy ? t('telegramQrRefreshing', 'Refreshing…') : t('telegramQrRefresh', 'Refresh code')}
                            </button>
                        </div>

                        {/* Fallback for anyone who DOES want the desktop client
                            (or is already reading this on their phone): the same
                            deep link, one tap away. */}
                        {qrLink?.deep_link && (
                            <div className="text-center">
                                <a
                                    data-testid="telegram-qr-open-link"
                                    href={qrLink.deep_link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-[12.5px] font-medium text-pai-blue hover:underline"
                                >
                                    {qrCfg.openLabel}
                                </a>
                            </div>
                        )}
                    </div>
                )}

                <div className={`space-y-[26px]${qrWorld ? ' hidden' : ''}`}>
                    {/* ── USER (account/identity/billing) view ── #236 */}
                    {/* Profile (User Settings #184) — the name PAI uses + registered
                        email, at the top of the modal. */}
                    {isUser && (
                    <div>
                        <h3 className={EYEBROW}>
                            {t('profileSection', 'Profile')}
                        </h3>
                        <div className={GROUP_CARD}>
                            <div className="px-[22px] py-[17px] border-b border-pai-hairline">
                                <label className={`block ${ROW_LABEL}`}>
                                    {t('userName', 'User Name')}
                                </label>
                                <div className={`${ROW_DESC} mb-3`}>{t('userNameDesc', 'The name PAI uses to address you.')}</div>
                                <div className="flex items-center gap-2.5">
                                    <input
                                        data-testid="settings-username"
                                        value={userName}
                                        onChange={(e) => { setUserName(e.target.value); setNameError(null); setNameSaved(false); }}
                                        onKeyDown={(e) => { if (e.key === 'Enter') saveUserName(); }}
                                        maxLength={50}
                                        className="pai-input flex-1 min-w-0 h-10 px-4 text-[14px]"
                                    />
                                    <button
                                        data-testid="settings-username-save"
                                        onClick={saveUserName}
                                        disabled={nameSaving}
                                        className="pai-btn-primary flex-shrink-0 disabled:opacity-40"
                                    >
                                        {t('save', 'Save')}
                                    </button>
                                </div>
                                {nameError && (
                                    <div data-testid="settings-username-error" className="mt-2 text-[12.5px] text-pai-red">{nameError}</div>
                                )}
                                {nameSaved && !nameError && (
                                    <div data-testid="settings-username-saved" className="mt-2 text-[12.5px] font-semibold text-pai-green">{t('saved', 'Saved')}</div>
                                )}
                            </div>
                            <div className="px-[22px] py-[17px]">
                                <label className={`block ${ROW_LABEL}`}>
                                    {t('registeredEmail', 'Registered Email')}
                                </label>
                                <div data-testid="settings-email" className="mt-2 text-[14px] text-pai-secondary break-all">
                                    {email || '—'}
                                </div>
                            </div>
                        </div>
                    </div>
                    )}

                    {/* Notification Center & Delivery Matrix (#191) — opens the
                        dedicated overlay with per-feature alert + channel controls. */}
                    {isSystem && (
                    <div>
                        <h3 className={EYEBROW}>
                            {t('navNotifications', 'Notifications')}
                        </h3>
                        <div className={GROUP_CARD}>
                            <div className={ROW}>
                                <div className={ROW_MAIN}>
                                    <Bell size={16} className="text-pai-meta flex-shrink-0" />
                                <div className="min-w-0">
                                        <div className={ROW_LABEL}>{t('navNotificationCenter', 'Notification Center')}</div>
                                        <div className={ROW_DESC}>{t('navNotificationCenterDesc', 'Choose which alerts you receive and where they land.')}</div>
                                </div>
                            </div>
                            <button
                                data-testid="settings-notifications"
                                onClick={() => setNotifCenterOpen(true)}
                                    className="pai-btn-secondary flex-shrink-0"
                            >
                                {t('commonOpen', 'Open')}
                            </button>
                            </div>
                        </div>
                    </div>
                    )}

                    {/* Salary and Payment (User Settings #184 → live billing,
                        #297-#299). The product sells a SALARY, never a
                        subscription (#336's terminology audit) — the section,
                        its rows and every string below say so. Payment Method
                        links to the Customer Portal, which owns the invoices. */}
                    {isUser && (
                    <div>
                        <h3 className={EYEBROW}>
                            {t('salarySection', 'Salary and Payment')}
                        </h3>
                        <div className={GROUP_CARD}>
                            <div className={ROW} data-testid="settings-subscription">
                                <div className={ROW_MAIN}>
                                    <Zap size={16} className="text-pai-amber flex-shrink-0" />
                                <div className="min-w-0">
                                        <div className={ROW_LABEL}>{t('salaryType', 'Salary type')}</div>
                                    {/* The CANONICAL tier name. `plan_canonical` is resolved
                                        backend-side precisely so the alias table is never
                                        re-implemented here — a stored 'max' must read as
                                        Partner Salary, not as "max". */}
                                        {/* The design's identity chip (ACCOUNT → "CREATOR"):
                                            a blue wash at 16% with blue-tint text. Here it
                                            marks the tier, which is this product's equivalent
                                            badge beside the account.
                                            ⚠️ Deliberately NOT `uppercase`: the chip's content
                                            is DATA, not a fixed label, and salary-for-
                                            subscription.spec.js / user-plans.spec.js read it
                                            with `innerText`, which applies text-transform — so
                                            an uppercase chip would report "EXECUTIVE" and fail
                                            the canonical-tier-name contract. */}
                                        <div className="mt-1.5">
                                            <span
                                                data-testid="settings-plan-name"
                                                className="inline-block rounded-chip bg-[rgba(74,144,240,0.16)] px-[11px] py-[5px] text-[11px] font-bold tracking-[0.06em] text-pai-blue-tint"
                                            >
                                        {(() => {
                                            const tier = planValue(planStatus) || 'free';
                                            return t(`plan_${tier}`, planLabel(tier));
                                        })()}
                                            </span>
                                    </div>
                                </div>
                            </div>
                            <button
                                type="button"
                                data-testid="settings-change-salary"
                                onClick={() => setChangeSalaryOpen(true)}
                                    className="pai-btn-secondary flex-shrink-0"
                            >
                                {t('changeSalary', 'Change Salary')}
                            </button>
                        </div>
                        {/* Consumption is reported in ENERGY POINTS, not dollars: points
                            are what the user is actually given and spends, and the USD cap
                            is an internal enforcement detail they never bought. */}
                            <div className={ROW} data-testid="settings-plan-usage">
                                <div className={ROW_MAIN}>
                                    <Zap size={16} className="text-pai-meta flex-shrink-0" />
                                    <div className={`min-w-0 ${ROW_LABEL}`}>
                                    {t('energyConsumedPct', 'Percentage of energy points consumed')}
                                </div>
                            </div>
                            <div
                                data-testid="settings-energy-consumed"
                                    className="text-[15px] font-bold text-white tabular-nums text-right flex-shrink-0"
                            >
                                {`${consumedPercent(planStatus?.energy)}%`}
                            </div>
                        </div>
                            {/* Payment method + payment HISTORY (#336). The card
                                asks the section to reflect "the new tier +
                                payment history". History is Stripe's — it owns
                                the invoices, and re-rendering a copy of them
                                here would go stale the moment one is amended or
                                refunded — so this is one click to the Customer
                                Portal, which is the real record. The row only
                                offers it when a billing account actually exists:
                                a tier an ADMIN granted has no payments to show,
                                and the portal would 409. */}
                            <div className={ROW} data-testid="settings-payment-method">
                                <div className={ROW_MAIN}>
                                    <CreditCard size={16} className="text-pai-meta flex-shrink-0" />
                                <div className="min-w-0">
                                        <div className={ROW_LABEL}>{t('paymentMethod', 'Payment Method')}</div>
                                        <div className={ROW_DESC} data-testid="settings-payment-desc">
                                            {planStatus?.has_billing_account
                                                ? t('paymentMethodStripe', 'Card and invoices managed securely by Stripe')
                                                : t('paymentMethodNone', 'No payment method on file')}
                                        </div>
                                </div>
                            </div>
                                {planStatus?.has_billing_account && (
                                    <button
                                        type="button"
                                        data-testid="settings-payment-history"
                                        disabled={billingBusy}
                                        onClick={openBillingPortal}
                                        className="pai-btn-secondary flex-shrink-0"
                                    >
                                        {t('paymentHistory', 'Payment history')}
                                    </button>
                                )}
                        </div>
                            {billingError && (
                                <div className={ROW}>
                                    <div data-testid="settings-payment-error"
                                         className="text-[13px] text-pai-rose">
                                        {billingError}
                                    </div>
                                </div>
                            )}
                        {/* The plan row above answers "what am I on"; the Hub answers
                            "where did it go". Same pattern as AI Personalization:
                            close the modal, then open the panel. (Energy HUB part 1) */}
                            <div className={ROW}>
                                <div className={ROW_MAIN}>
                                    <Zap size={16} className="text-pai-meta flex-shrink-0" />
                                <div className="min-w-0">
                                        <div className={ROW_LABEL}>{t('energyHub', 'Energy Hub')}</div>
                                        <div className={ROW_DESC}>{t('energyHubDesc', 'See exactly where your energy goes.')}</div>
                                </div>
                            </div>
                            <button
                                data-testid="settings-energy-open"
                                onClick={() => {
                                    onClose();
                                    if (onOpenEnergyPanel) onOpenEnergyPanel();
                                    else navigate('/energy');
                                }}
                                    className="pai-btn-secondary flex-shrink-0"
                            >
                                {t('energyOpenHub', 'View Energy Hub')}
                            </button>
                        </div>
                    </div>
                    </div>
                    )}

                    {/* ── SYSTEM (app-wide preferences) view ── #236 */}
                    {isSystem && SHOW_THEME_TOGGLE && onToggleTheme && (
                        <div>
                            <h3 className={EYEBROW}>
                                {t('appearance')}
                            </h3>
                            <div className={GROUP_CARD}>
                                <div className={ROW}>
                                    <div className={ROW_MAIN}>
                                    {theme === 'dark' ? (
                                            <Moon size={16} className="text-pai-meta flex-shrink-0" />
                                    ) : (
                                            <Sun size={16} className="text-pai-meta flex-shrink-0" />
                                    )}
                                        <div className="min-w-0">
                                            <div className={ROW_LABEL}>{t('darkMode')}</div>
                                            <div className={ROW_DESC}>
                                            {t('darkModeDesc')}
                                        </div>
                                    </div>
                                </div>
                                <button
                                    onClick={onToggleTheme}
                                        className={switchTrack(theme === 'dark')}
                                    role="switch"
                                    aria-checked={theme === 'dark'}
                                >
                                        <span className={switchKnob(theme === 'dark')} />
                                </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {isSystem && (<>
                    {/* Language selector (Multilanguage #123) */}
                    <div>
                        <h3 className={EYEBROW}>
                            {t('language')}
                        </h3>
                        <div className={GROUP_CARD}>
                            <div className={ROW}>
                                <div className={ROW_MAIN}>
                                    <Globe size={16} className="text-pai-meta flex-shrink-0" />
                                    <div className="min-w-0">
                                        <div className={ROW_LABEL}>{t('language')}</div>
                                        <div className={ROW_DESC}>{t('languageDesc')}</div>
                                </div>
                            </div>
                                {/* Segmented control: 4px inset wrapper, radius-11 outer /
                                    radius-8 segments, the selected one on a 10% wash. */}
                                <div className="inline-flex flex-shrink-0 gap-1 rounded-ctl border border-pai bg-white/[0.03] p-1" data-testid="language-toggle">
                                {LANGUAGES.map((l) => (
                                    <button
                                        key={l.code}
                                        type="button"
                                        data-testid={`language-option-${l.code}`}
                                        onClick={() => setLanguage(l.code)}
                                            className={`rounded-seg px-[15px] py-2 text-[13.5px] font-semibold transition-colors ${
                                            language === l.code
                                                    ? 'bg-white/[0.1] text-white'
                                                    : 'text-white/50 hover:text-white'
                                        }`}
                                        aria-pressed={language === l.code}
                                    >
                                        {l.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                    </div>

                    {/* Default / base reference currency (Default Currency #124) */}
                    <div>
                        <h3 className={EYEBROW}>
                            {t('currency')}
                        </h3>
                        <div className={GROUP_CARD}>
                            <div className={ROW}>
                                <div className={ROW_MAIN}>
                                    <Wallet size={16} className="text-pai-meta flex-shrink-0" />
                                    <div className="min-w-0">
                                        <div className={ROW_LABEL}>{t('baseCurrency')}</div>
                                        <div className={ROW_DESC}>{t('baseCurrencyDesc')}</div>
                                </div>
                            </div>
                            <select
                                data-testid="settings-base-currency"
                                value={baseCurrency}
                                onChange={(e) => confirmPreferenceSave(setCurrency(e.target.value))}
                                    style={SELECT_CHEVRON}
                                    className={`${SELECT} flex-shrink-0`}
                            >
                                    {(currencyList || []).map((c) => (<option key={c} value={c} className={OPTION}>{c}</option>))}
                            </select>
                            </div>
                        </div>
                    </div>

                    {/* System reference timezone (Default Timezone #125) — searchable */}
                    <div>
                        <h3 className={EYEBROW}>
                            {t('timezone')}
                        </h3>
                        <div className={GROUP_CARD}>
                            <div className={ROW}>
                                <div className={ROW_MAIN}>
                                    <Clock size={16} className="text-pai-meta flex-shrink-0" />
                                <div className="min-w-0">
                                        <div className={ROW_LABEL}>{t('timezone')}</div>
                                        <div className={`${ROW_DESC} truncate`}>{t('timezoneDesc')}</div>
                                </div>
                            </div>
                            <input
                                list="omnis-tz-list"
                                data-testid="settings-timezone"
                                value={tzDraft}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    setTzDraft(v);
                                    tzEditedRef.current = true;
                                    // An exact zone (a datalist pick, a paste) commits at once.
                                    if ((timezoneList || []).includes(v)) commitTimezone(v);
                                }}
                                onBlur={(e) => commitTimezone(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitTimezone(e.currentTarget.value); } }}
                                placeholder={t('navSearchTimezone', 'Search timezone…')}
                                    className="pai-input w-44 flex-shrink-0 h-10 px-4 text-[14px] font-medium placeholder:text-pai-placeholder"
                            />
                            <datalist id="omnis-tz-list">
                                {(timezoneList || []).map((z) => (<option key={z} value={z} />))}
                            </datalist>
                        </div>
                    </div>
                    </div>

                    {/* Recently Deleted retention policy (#127) */}
                    <div>
                        <h3 className={EYEBROW}>
                            {t('recentlyDeleted')}
                        </h3>
                        <div className={GROUP_CARD}>
                            <div className={ROW}>
                                <div className={ROW_MAIN}>
                                    <Trash2 size={16} className="text-pai-meta flex-shrink-0" />
                                <div className="min-w-0">
                                        <div className={ROW_LABEL}>{t('retentionPeriod')}</div>
                                        <div className={`${ROW_DESC} truncate`}>{t('retentionPeriodDesc')}</div>
                                </div>
                            </div>
                            <select
                                data-testid="settings-retention"
                                value={retentionPolicy}
                                onChange={(e) => {
                                    // Synced (#480) but deliberately NO "Saved" toast — only
                                    // currency + timezone confirm (PO rule).
                                    Promise.resolve(setRetentionPolicy(e.target.value)).catch(() => { /* reverted by the hook */ });
                                }}
                                    style={SELECT_CHEVRON}
                                    className={`${SELECT} flex-shrink-0`}
                            >
                                    {retentionOptions.map((o) => (<option key={o.value} value={o.value} className={OPTION}>{o.label}</option>))}
                            </select>
                            </div>
                        </div>
                    </div>
                    </>)}

                    {isUser && (<>
                    {/* PAI Tools — the full agentic Telegram bot (QR handshake).
                        Branded by the BOT, not the platform (#327): the sibling
                        row below is "PAI Conversations", and naming one by its
                        service and the other by its transport is exactly the
                        inconsistency the card reports. */}
                    <div>
                        <div className={EYEBROW_ROW}>
                            <h3 className="pai-eyebrow block">
                                {t('telegramToolsAccount', 'PAI Tools')}
                            </h3>
                            <InfoTooltip
                                testId="settings-info-telegram-tools"
                                bubbleTestId="settings-info-tooltip"
                                label={t('settingsInfoAbout', 'About {name}', { name: 'PAI Tools' })}
                                text={t('telegramAccountDesc')}
                            />
                        </div>
                        <div className={GROUP_CARD}>
                            <div className={ROW_COMPACT}>
                                <div className={ROW_MAIN}>
                                    <Send size={16} className="text-pai-blue flex-shrink-0" />
                                    <div className={`${ROW_LABEL} min-w-0`}>{t('telegramToolsAccount', 'PAI Tools')}</div>
                            </div>
                                <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                                {telegramLinked ? (
                                        <div className="flex items-center gap-2.5">
                                            <span data-testid="settings-telegram-linked" className="text-[12.5px] font-semibold text-pai-green flex items-center gap-1.5">
                                                <span className="w-2 h-2 rounded-full bg-pai-green" /> {t('telegramLinkedLabel')}
                                        </span>
                                        <button
                                            data-testid="settings-telegram-unlink"
                                            onClick={unlinkTelegram}
                                            disabled={telegramBusy}
                                                className="pai-btn-secondary disabled:opacity-40"
                                        >
                                            {t('telegramUnlink')}
                                        </button>
                                    </div>
                                ) : (
                                    <button
                                        data-testid="settings-telegram-connect"
                                        onClick={() => openTelegramQr('tools')}
                                        disabled={telegramBusy}
                                            className="pai-btn-primary disabled:opacity-40"
                                    >
                                        {t('telegramConnect')}
                                    </button>
                                )}
                                {telegramAwaiting && !telegramLinked && !telegramError && (
                                        <div data-testid="settings-telegram-awaiting" className="text-[12.5px] text-pai-meta max-w-[16rem] text-right">
                                        {t('telegramOpenHint')}
                                    </div>
                                )}
                                {telegramError && (
                                        <div data-testid="settings-telegram-error" className="text-[12.5px] text-pai-red max-w-[16rem] text-right">
                                        {telegramError}
                                    </div>
                                )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* PAI Conversations — the fast-track bot (Dual-Bucket System).
                        A SECOND bot with its own handshake, deliberately shown as
                        its own row: the two are connected independently, and this
                        one is NOT a notification channel. */}
                    <div>
                        <div className={EYEBROW_ROW}>
                            <h3 className="pai-eyebrow block">
                                {t('telegramConvoAccount')}
                            </h3>
                            <InfoTooltip
                                testId="settings-info-telegram-convo"
                                bubbleTestId="settings-info-tooltip"
                                label={t('settingsInfoAbout', 'About {name}', { name: 'PAI Conversations' })}
                                text={t('telegramConvoAccountDesc')}
                            />
                        </div>
                        <div className={GROUP_CARD}>
                            <div className={ROW_COMPACT}>
                                <div className={ROW_MAIN}>
                                    <Zap size={16} className="text-pai-blue flex-shrink-0" />
                                    <div className={`${ROW_LABEL} min-w-0`}>{t('telegramConvoAccount')}</div>
                            </div>
                                <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                                {convoLinked ? (
                                        <div className="flex items-center gap-2.5">
                                            <span data-testid="settings-telegram-convo-linked" className="text-[12.5px] font-semibold text-pai-green flex items-center gap-1.5">
                                                <span className="w-2 h-2 rounded-full bg-pai-green" /> {t('telegramConvoLinkedLabel')}
                                        </span>
                                        <button
                                            data-testid="settings-telegram-convo-unlink"
                                            onClick={unlinkConvo}
                                            disabled={convoBusy}
                                                className="pai-btn-secondary disabled:opacity-40"
                                        >
                                            {t('telegramConvoUnlink')}
                                        </button>
                                    </div>
                                ) : (
                                    <button
                                        data-testid="settings-telegram-convo-connect"
                                        onClick={() => openTelegramQr('convo')}
                                        disabled={convoBusy}
                                            className="pai-btn-primary disabled:opacity-40"
                                    >
                                        {t('telegramConvoConnect')}
                                    </button>
                                )}
                                {convoAwaiting && !convoLinked && !convoError && (
                                        <div data-testid="settings-telegram-convo-awaiting" className="text-[12.5px] text-pai-meta max-w-[16rem] text-right">
                                        {t('telegramConvoOpenHint')}
                                    </div>
                                )}
                                {convoError && (
                                        <div data-testid="settings-telegram-convo-error" className="text-[12.5px] text-pai-red max-w-[16rem] text-right">
                                        {convoError}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    </div>

                    {/* Ghost Mode (Agenda TO Contacts — Phase A, T7) */}
                    <div>
                        <h3 className={EYEBROW}>
                            {t('privacy')}
                        </h3>
                        <div className={GROUP_CARD}>
                            <div className={ROW}>
                                <div className={ROW_MAIN}>
                                    <EyeOff size={16} className="text-pai-meta flex-shrink-0" />
                                <div className="min-w-0">
                                        <div className={ROW_LABEL}>{t('ghostMode')}</div>
                                        <div className={ROW_DESC}>{t('ghostModeDesc')}</div>
                                </div>
                            </div>
                            <button
                                data-testid="settings-ghost-mode"
                                onClick={toggleGhostMode}
                                disabled={ghostSaving}
                                    className={`${switchTrack(ghostMode)} disabled:opacity-60`}
                                role="switch"
                                aria-checked={ghostMode}
                            >
                                    <span className={switchKnob(ghostMode)} />
                            </button>
                            </div>
                        </div>
                    </div>

                    {/* Security — two-step verification (#333). */}
                    <SecuritySection mfa={mfaInfo} onMfaChange={setMfaInfo} />

                    {/* Legal — the iubenda-hosted Privacy Policy, Cookie Policy
                        and Terms. Lives in the USER view (it is about this
                        person's relationship with the service, not an app-wide
                        preference) and above the Danger Zone, so the documents
                        are reachable from inside the app and not only from the
                        signed-out screens. */}
                    <div data-testid="settings-legal">
                        <h3 className={EYEBROW}>
                            {t('settingsLegalTitle', 'Legal')}
                        </h3>
                        <div className={GROUP_CARD}>
                            <div className={ROW}>
                                <div className={ROW_MAIN}>
                                    <FileText size={16} className="text-pai-meta flex-shrink-0" />
                                    <div className="min-w-0">
                                        <div className={ROW_LABEL}>{t('settingsLegalDocuments', 'Policies and terms')}</div>
                                        <div className={ROW_DESC}>{t('settingsLegalDesc', 'Read the documents that govern your use of PAI.')}</div>
                                    </div>
                                </div>
                                <LegalLinks className="min-w-0" align="end" />
                            </div>
                            {/* Manage cookie preferences (Iubenda bug #401).
                                iubenda's persistent floating badge — which used
                                to sit over the bottom-right of the workspace —
                                is switched off on their side, so this is now
                                the way back into the consent panel. Disabled
                                (never hidden) when the widget has not loaded:
                                a control that vanishes teaches nothing, while a
                                disabled one with a reason is honest. */}
                            <div className={ROW}>
                                <div className={ROW_MAIN}>
                                    <Cookie size={16} className="text-pai-meta flex-shrink-0" />
                                    <div className="min-w-0">
                                        <div className={ROW_LABEL}>{t('settingsCookiePrefs', 'Cookie preferences')}</div>
                                        <div className={ROW_DESC}>{t('settingsCookiePrefsDesc', 'Review or change the tracking choices you made.')}</div>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    data-testid="settings-cookie-preferences"
                                    onClick={openCookiePreferences}
                                    disabled={!cookiePrefsAvailable}
                                    title={cookiePrefsAvailable ? undefined : t('settingsCookiePrefsUnavailable', 'The consent widget could not be loaded.')}
                                    className="flex-shrink-0 rounded-ctl border border-pai-subtle bg-white/[0.04] px-4 py-2 text-[13px] font-semibold text-pai-secondary transition-colors hover:border-pai-strong hover:text-white disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-pai-subtle disabled:hover:text-pai-secondary"
                                >
                                    {t('settingsCookiePrefsAction', 'Manage')}
                                </button>
                            </div>
                        </div>
                    </div>
                    </>)}

                    {/* #147 "Soulfile": the "AI Personalization" entry (the
                        SOUL.md / USER.md editor) was REMOVED here. PAI's core
                        directives are now a hidden, static base layer in the
                        system instruction, and the per-user SOUL/USER files
                        became an internal personalization store that the
                        learning service maintains on its own. The user's
                        explicit steering channel is the Rulebook panel. */}
                    {isSystem && (<>

                    {hasMonitoringAccess && (
                        <div>
                            <h3 className={EYEBROW}>
                                {t('navAdmin', 'Admin')}
                            </h3>
                            <div className={GROUP_CARD}>
                                <div className={ROW}>
                                    <div className={ROW_MAIN}>
                                        <BarChart3 size={16} className="text-pai-amber flex-shrink-0" />
                                        <div className="min-w-0">
                                            <div className={ROW_LABEL}>{t('navUsageDashboard', 'Usage Dashboard')}</div>
                                            <div className={ROW_DESC}>
                                            {t('navUsageDashboardDesc', 'Monitor token usage, costs, and pipeline breakdown')}
                                        </div>
                                    </div>
                                </div>
                                <button
                                    onClick={() => { onClose(); navigate('/dashboard'); }}
                                        className="pai-btn-secondary flex-shrink-0"
                                >
                                    {t('commonOpen', 'Open')}
                                </button>
                                </div>
                            </div>
                        </div>
                    )}
                    </>)}

                    {/* Account (Logout) — a distinct control from the destructive
                        Delete below it (#236: logout lives INSIDE the User Settings
                        view now, not the rail popover). */}
                    {isUser && (
                    <div>
                        <h3 className={EYEBROW}>
                            {t('accountSection', 'Account')}
                        </h3>
                        <div className={GROUP_CARD}>
                            <div className={ROW}>
                                <div className={ROW_MAIN}>
                                    <LogOut size={16} className="text-pai-meta flex-shrink-0" />
                                <div className="min-w-0">
                                        <div className={ROW_LABEL}>{t('logout', 'Logout')}</div>
                                        <div className={`${ROW_DESC} truncate`}>{t('logoutDesc', 'Sign out of your account on this device.')}</div>
                                </div>
                            </div>
                                {/* The design makes Sign Out a danger button. */}
                            <button
                                data-testid="settings-logout"
                                onClick={doLogout}
                                    className="pai-btn-danger flex-shrink-0"
                            >
                                <LogOut size={14} /> {t('logout', 'Logout')}
                            </button>
                            </div>
                        </div>
                    </div>
                    )}

                    {/* Danger Zone — permanent account deletion (Agenda TO Contacts
                        Phase D / T1 "Smithereens") */}
                    {isUser && (
                    <div>
                        <h3 className="pai-eyebrow block mb-3 text-pai-red">
                            {t('dangerZone')}
                        </h3>
                        {/* The one tinted group: a red border + wash at the design's
                            semantic-row alphas, so the zone reads as dangerous
                            without an accent-FILLED panel. */}
                        <div className="rounded-card border border-pai-red/[0.32] bg-pai-red/[0.04] overflow-hidden">
                            <div className={ROW}>
                                <div className={ROW_MAIN}>
                                    <AlertTriangle size={16} className="text-pai-red flex-shrink-0" />
                                <div className="min-w-0">
                                        <div className={ROW_LABEL}>{t('deleteAccount')}</div>
                                        <div className={`${ROW_DESC} truncate`}>{t('deleteAccountDesc')}</div>
                                </div>
                            </div>
                            <button
                                data-testid="settings-delete-account"
                                onClick={openDeleteAccount}
                                    className="pai-btn-danger flex-shrink-0"
                            >
                                {t('deleteAccount')}
                            </button>
                            </div>
                        </div>
                    </div>
                    )}
                </div>
            </Modal>

            {/* Change Salary (#312) — the tiers, with the current one marked, and
                an honest note that switching arrives with the payments release.
                Read-only by design: the only endpoint that can move a plan today
                is the ADMIN one, and wiring a user-facing button to it would let
                any account promote itself for free. */}
            {changeSalaryOpen && (
                <ChangeSalaryDialog
                    current={planValue(planStatus) || 'free'}
                    onClose={() => setChangeSalaryOpen(false)}
                />
            )}

            {/* Notification Center overlay (#191) — its own fixed layer above the
                Settings backdrop so it can't be dismissed by a backdrop click; the
                component owns its list ↔ sub-menu navigation + Back/Close. */}
            {notifCenterOpen && (
                <Modal
                    onClose={() => setNotifCenterOpen(false)}
                    zIndex="z-[75]"
                    closeOnBackdrop={false}
                    closeOnEscape={false}
                    scrimTestId="notification-center-overlay"
                    cardClassName="p-7 max-h-[85dvh]"
                >
                        <NotificationCenter
                            onExit={() => setNotifCenterOpen(false)}
                            onClose={onClose}
                        />
                    </Modal>
            )}

            {/* Account-deletion confirmation modal — its own fixed overlay (higher
                z-index than the Settings backdrop) so clicking it can't dismiss
                Settings; the destructive DELETE is gated behind an exact typed
                "DELETE". */}
            {showDeleteAccount && (
                <Modal
                    onClose={closeDeleteAccount}
                    zIndex="z-[80]"
                    layout="column"
                    closeOnEscape={false}
                    scrimTestId="account-delete-modal"
                    cardTestId="account-delete-card"
                    surface="bg-pai-modal rounded-modal shadow-modal border border-pai-red/[0.35]"
                    cardClassName="p-5 sm:p-7"
                >
                    {/* Delete screen #430. The card is BOUNDED to the viewport (dvh, so a
                        phone's collapsing URL bar can't push it off) and is a flex
                        column: each step is a scrolling BODY plus a footer that never
                        scrolls away — so the final Delete button is reachable at any
                        window size. It used to be an unbounded card inside a centred
                        flex overlay: taller than the window it simply overflowed both
                        edges, with nothing scrollable and the button below the fold.
                        <Modal layout="column"> is that bounded flex column. */}
                        {/* Step 1 — Data Sovereignty: download a full ZIP of your data
                            BEFORE deleting (User Settings #184). */}
                        {deleteStep === 'export' && (
                            <div data-testid="account-delete-export-step" className="flex flex-col min-h-0">
                                <div data-testid="account-delete-body" className="min-h-0 overflow-y-auto overscroll-contain -mr-2 pr-2">
                                <div className="flex items-center gap-2 mb-3">
                                    <Download size={20} className="text-pai-blue flex-shrink-0" />
                                    <h2 className="text-[19px] font-bold text-white">{t('exportModalTitle', 'Download all your data')}</h2>
                                </div>
                                <p className="text-[14.5px] leading-relaxed text-pai-secondary mb-5">
                                    {t('exportModalBody', 'Before deleting your account, download a complete copy of your data. The ZIP contains every conversation and note (as markdown), your uploaded documents and images, your boards, and your accounting, calendar, reminder and contact records.')}
                                </p>
                                <button
                                    data-testid="account-export-download"
                                    onClick={downloadExport}
                                    disabled={exportBusy}
                                    aria-busy={exportBusy}
                                    className="pai-btn-primary w-full h-11 disabled:opacity-40"
                                >
                                    {exportBusy
                                        ? <Loader2 size={16} data-testid="account-export-spinner" className="animate-spin" />
                                        : <Download size={16} />}
                                    {exportBusy ? t('exportBusy', 'Generating archive…') : t('exportDownloadBtn', 'Download All Data (ZIP)')}
                                </button>
                                {/* A full archive can take a minute for a large account (#331),
                                    so the wait needs to be explained, not just spun at. */}
                                {exportBusy && (
                                    <div data-testid="account-export-status" role="status" aria-live="polite" className="mt-2.5 text-[12.5px] text-pai-meta">
                                        {t('exportBusyHint', 'Collecting your conversations, documents and records. This can take a minute for a large account — keep this window open.')}
                                    </div>
                                )}
                                {exportDone && !exportError && (
                                    <div data-testid="account-export-done" className="mt-2.5 text-[12.5px] font-semibold text-pai-green">
                                        {t('exportDone', 'Your data has been downloaded.')}
                                    </div>
                                )}
                                {exportError && (
                                    <div className="mt-2 flex items-start justify-between gap-2">
                                        <div data-testid="account-export-error" className="text-[12.5px] text-pai-red min-w-0 break-words">{exportError}</div>
                                        <button
                                            data-testid="account-export-retry"
                                            onClick={downloadExport}
                                            disabled={exportBusy}
                                            className="flex-shrink-0 inline-flex items-center gap-1.5 rounded-chip border border-pai px-2.5 py-1.5 text-xs font-semibold text-pai-secondary transition-colors hover:bg-white/[0.07] hover:text-white disabled:opacity-50"
                                        >
                                            <RotateCcw size={12} /> {t('exportRetry', 'Retry')}
                                        </button>
                                    </div>
                                )}
                                </div>
                                <div data-testid="account-delete-footer" className="flex-shrink-0 pt-4 flex flex-wrap justify-end gap-2">
                                    <button
                                        data-testid="account-delete-cancel"
                                        onClick={closeDeleteAccount}
                                        disabled={exportBusy}
                                        className="pai-btn-ghost disabled:opacity-40"
                                    >
                                        {t('deleteAccountCancel')}
                                    </button>
                                    <button
                                        data-testid="account-delete-continue"
                                        onClick={() => setDeleteStep('confirm')}
                                        disabled={exportBusy}
                                        className="pai-btn-danger disabled:opacity-40"
                                    >
                                        {t('exportContinueBtn', 'Continue to delete')}
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Step 2 — final typed-DELETE confirmation → the wipe. */}
                        {deleteStep === 'confirm' && (
                            <div data-testid="account-delete-confirm-step" className="flex flex-col min-h-0">
                                <div data-testid="account-delete-body" className="min-h-0 overflow-y-auto overscroll-contain -mr-2 pr-2">
                                <div className="flex items-center gap-2 mb-3">
                                    <AlertTriangle size={20} className="text-pai-red flex-shrink-0" />
                                    <h2 className="text-[19px] font-bold text-pai-red">{t('deleteAccountModalTitle')}</h2>
                                </div>
                                <ul className="space-y-1.5 mb-5 text-[14.5px] leading-relaxed text-pai-secondary list-disc pl-5 marker:text-white/30">
                                    <li>{t('deleteAccountWarnErase')}</li>
                                    <li>{t('deleteAccountWarnShared')}</li>
                                    <li>{t('deleteAccountWarnBooks')}</li>
                                    <li>{t('deleteAccountWarnBilling')}</li>
                                    <li className="font-semibold text-pai-red list-none -ml-5">{t('deleteAccountWarnFinal')}</li>
                                </ul>
                                {/* The honest residue. "Everything, immediately" is
                                    true, and three things still outlive the wipe —
                                    saying so here is part of keeping the promise
                                    rather than an asterisk on it. Kept visually
                                    quieter than the warnings above (this is
                                    reassurance, not another danger), and enumerated
                                    in BE/ACCOUNT_DELETION_AUDIT.md §6. */}
                                <div
                                    data-testid="account-delete-residue"
                                    className="mb-5 rounded-card border border-pai-hairline bg-white/[0.02] px-3.5 py-3"
                                >
                                    <div className="text-[12px] font-semibold uppercase tracking-wide text-pai-secondary/70 mb-1.5">
                                        {t('deleteAccountResidueTitle')}
                                    </div>
                                    <ul className="space-y-1 text-[13px] leading-relaxed text-pai-secondary/80 list-disc pl-4 marker:text-white/20">
                                        <li>{t('deleteAccountResidueBackups')}</li>
                                        <li>{t('deleteAccountResidueLegal')}</li>
                                    </ul>
                                </div>
                                <label className="block text-[13px] font-semibold text-pai-secondary mb-2">
                                    {t('deleteAccountConfirmLabel')}
                                </label>
                                <input
                                    data-testid="account-delete-input"
                                    value={deleteConfirmText}
                                    onChange={(e) => { setDeleteConfirmText(e.target.value); setDeleteError(null); }}
                                    placeholder="DELETE"
                                    autoComplete="off"
                                    className="pai-input w-full h-11 px-4 text-[14px] font-semibold tracking-[0.08em] placeholder:text-pai-placeholder placeholder:tracking-normal placeholder:font-normal"
                                />
                                {deleteError && (
                                    <div data-testid="account-delete-error" className="mt-2 text-[12.5px] text-pai-red">{deleteError}</div>
                                )}
                                </div>
                                <div data-testid="account-delete-footer" className="flex-shrink-0 pt-4 flex flex-wrap justify-between gap-2">
                                    <button
                                        data-testid="account-delete-back"
                                        onClick={() => setDeleteStep('export')}
                                        disabled={deleteBusy}
                                        className="pai-btn-ghost disabled:opacity-40"
                                    >
                                        {t('back', 'Back')}
                                    </button>
                                    <div className="flex gap-2">
                                        <button
                                            data-testid="account-delete-cancel"
                                            onClick={closeDeleteAccount}
                                            disabled={deleteBusy}
                                            className="pai-btn-ghost disabled:opacity-40"
                                        >
                                            {t('deleteAccountCancel')}
                                        </button>
                                        <button
                                            data-testid="account-delete-confirm"
                                            onClick={confirmDeleteAccount}
                                            disabled={deleteBusy || deleteConfirmText !== 'DELETE'}
                                            className="pai-btn-danger disabled:opacity-40 disabled:cursor-not-allowed"
                                        >
                                            {deleteBusy ? t('deleteAccountBusy') : t('deleteAccountConfirmBtn')}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </Modal>
            )}
        </>
    );
};

export default SettingsModal;
